import React from 'react';
import { Plus, Bookmark, Pencil } from 'lucide-react';
import en from './locales/en';
import { makeT } from './locales';
import track from './analytics';
import { fetchTopAttractions } from './placesCache';

export const IDLE_CITY = { phase: 'idle', questions: [], suggestions: [], freeform: null, freeformVisible: false, freeformText: '', lastQuestion: null };
export const IDLE_ATTR = { phase: 'idle', questions: [], suggestions: [], mode: null, freeform: null, freeformVisible: false, freeformText: '', lastQuestion: null };

const defaultT = makeT(en);

async function callSuggest(body) {
  const res = await fetch('/api/suggest', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('API error');
  return res.json();
}

// CityDiscoveryPanel — controlled component for "Where to next?" flows.
// State lives in parent (via disc/setDisc) so it survives tab switches.
// setDisc receives a patch object that is merged into disc by the parent.
export function CityDiscoveryPanel({
  disc: rawDisc,
  setDisc,
  context,
  existingCityNames = new Set(),
  onAdd,
  onSnip,
  onDismiss,
  theme = 'light',
  triggerLabel,
  addLabel,
  t: tProp,
  lang,
  autoOpen = false,
}) {
  const t = tProp || defaultT;
  const disc = rawDisc || IDLE_CITY;
  const dark = theme === 'dark';
  const resolvedTriggerLabel = triggerLabel ?? t('where_to_next_trigger', null);
  const resolvedAddLabel = addLabel ?? t('add');

  const loadQuestions = async () => {
    track('discovery_panel_opened', { panel: 'city' });
    setDisc({ phase: 'loading', questions: [], suggestions: [], freeformVisible: false, freeformText: '' });
    try {
      const data = await callSuggest({ type: 'city-questions', ...context, language: lang });
      setDisc({ phase: 'questions', questions: data.questions || [], freeform: data.freeform });
    } catch {
      setDisc({ phase: 'idle' });
    }
  };

  const selectQuestion = async (text) => {
    setDisc({ phase: 'followup-loading', suggestions: [], lastQuestion: text });
    try {
      const data = await callSuggest({ type: 'city-followup', ...context, question: text, language: lang });
      if (data.error || !data.cities?.length) setDisc({ phase: 'error' });
      else setDisc({ phase: 'suggestions', suggestions: data.cities });
    } catch {
      setDisc({ phase: 'error' });
    }
  };

  const submitFreeform = async (text) => {
    if (!text.trim()) return;
    await selectQuestion(text);
    setDisc({ freeformVisible: false, freeformText: '' });
  };

  const handleDismiss = onDismiss || (() => { track('discovery_panel_dismissed', { panel: 'city' }); setDisc({ ...IDLE_CITY }); });

  const autoOpenFiredRef = React.useRef(false);
  React.useEffect(() => {
    if (autoOpen && !autoOpenFiredRef.current && disc.phase === 'idle') {
      autoOpenFiredRef.current = true;
      loadQuestions();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const s = dark ? {
    trigger: 'text-xs text-white/50 hover:text-white transition-colors',
    wrapper: 'space-y-2',
    loadText: 'text-xs text-white/60 animate-pulse',
    errText: 'text-xs text-red-300',
    retryBtn: 'text-xs text-white hover:text-blue-200 transition-colors',
    backSmall: 'text-xs text-white/60 hover:text-white transition-colors',
    card: 'flex items-start gap-2 bg-white/10 rounded-xl px-3 py-2.5',
    name: 'text-xs font-medium text-white',
    reason: 'text-xs text-white/70 mt-0.5',
    addBtn: 'flex-shrink-0 mt-0.5 flex items-center gap-1 text-xs px-2.5 py-1 bg-white/20 hover:bg-white/30 text-white rounded-full transition-colors',
    snipBtn: 'flex-shrink-0 mt-0.5 p-1.5 text-white/40 hover:text-white/80 transition-colors rounded',
    backBtn: 'text-xs text-white/50 hover:text-white transition-colors',
    dismissBtn: 'text-xs text-white/30 hover:text-white/60 transition-colors',
    questionBtn: 'block w-full text-left text-xs px-3 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-colors',
    header: 'text-xs text-white/50',
    freeformBtn: 'block w-full text-left text-xs px-3 py-2 text-white/40 hover:text-white transition-colors',
    input: 'flex-1 text-xs border border-white/20 rounded-lg px-2.5 py-1.5 focus:outline-none bg-white/10 text-white placeholder-white/40 focus:border-white/40',
    goBtn: 'text-xs px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg',
    noResults: 'text-xs text-white/50',
  } : {
    trigger: 'w-full text-sm text-blue-400 hover:text-blue-600 py-2 transition-colors',
    wrapper: 'bg-blue-50 rounded-xl px-4 py-3 space-y-2',
    loadText: 'text-xs text-blue-400 animate-pulse',
    errText: 'text-xs text-red-400',
    retryBtn: 'text-xs text-blue-500 hover:text-blue-700 transition-colors',
    backSmall: 'text-xs text-gray-400 hover:text-gray-500 transition-colors',
    card: 'flex items-start gap-2 bg-white rounded-xl px-3 py-2.5 shadow-sm',
    name: 'text-xs font-medium text-gray-800',
    reason: 'text-xs text-gray-500 mt-0.5',
    addBtn: 'flex-shrink-0 mt-0.5 flex items-center gap-1 text-xs px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors',
    snipBtn: 'flex-shrink-0 mt-0.5 p-1.5 text-gray-300 hover:text-blue-400 transition-colors rounded',
    backBtn: 'text-xs text-blue-400 hover:text-blue-600 transition-colors',
    dismissBtn: 'text-xs text-blue-300 hover:text-blue-400 transition-colors',
    questionBtn: 'block w-full text-left text-xs px-3 py-2 bg-white hover:bg-blue-100 rounded-xl text-gray-700 transition-colors shadow-sm',
    header: 'text-xs text-blue-500 font-medium',
    freeformBtn: 'block w-full text-left text-xs px-3 py-2 text-gray-400 hover:text-blue-500 transition-colors',
    input: 'flex-1 text-xs border border-blue-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-blue-400 bg-white',
    goBtn: 'text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700',
    noResults: 'text-xs text-gray-400',
  };

  const FreeformEntry = () => (
    <>
      {!disc.freeformVisible && (
        <button onClick={() => setDisc({ freeformVisible: true })} className={s.freeformBtn}>{t('freeform_cta')}</button>
      )}
      {disc.freeformVisible && (
        <div className="flex gap-1.5">
          <input autoFocus value={disc.freeformText || ''} onChange={e => setDisc({ freeformText: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') submitFreeform(disc.freeformText || ''); }} placeholder={t('describe_place')} className={s.input} />
          <button onClick={() => submitFreeform(disc.freeformText || '')} className={s.goBtn}>{t('go')}</button>
        </div>
      )}
    </>
  );

  if (disc.phase === 'idle') return <button onClick={loadQuestions} className={s.trigger}>{resolvedTriggerLabel}</button>;

  const filtered = (disc.suggestions || []).filter(sug => !existingCityNames.has(sug.name.toLowerCase()));

  if (disc.phase === 'loading' || disc.phase === 'followup-loading') {
    return <div className={s.wrapper}><p className={s.loadText}>{t('thinking_where')}</p></div>;
  }
  if (disc.phase === 'error') {
    return (
      <div className={`${s.wrapper} space-y-2`}>
        <p className={s.errText}>{t('couldnt_load_suggestions')}</p>
        <div className="flex gap-2">
          <button onClick={() => selectQuestion(disc.lastQuestion)} className={s.retryBtn}>{t('retry')}</button>
          <button onClick={() => setDisc({ phase: 'questions' })} className={s.backSmall}>{t('back')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className={s.wrapper}>
      {disc.phase === 'suggestions' && (
        <div className="space-y-1.5">
          {filtered.length > 0 ? filtered.map(sug => (
            <div key={sug.name} className={s.card}>
              <div className="flex-1 min-w-0">
                <span className={s.name}>{sug.name}</span>
                <p className={s.reason}>{sug.reason}</p>
              </div>
              {onSnip && <button onClick={() => onSnip('city', { name: sug.name })} className={s.snipBtn} title={t('snip')}><Bookmark className="w-4 h-4" /></button>}
              <button onClick={() => { track('discovery_panel_suggestion_used', { panel: 'city', suggestion_name: sug.name }); onAdd(sug.name); }} className={s.addBtn}><Plus className="w-3 h-3" />{resolvedAddLabel}</button>
            </div>
          )) : <p className={s.noResults}>{t('no_new_suggestions')}</p>}
          <button onClick={() => setDisc({ phase: 'questions', suggestions: [] })} className={s.backBtn}>{t('back_to_questions')}</button>
          <FreeformEntry />
        </div>
      )}
      {disc.phase === 'questions' && (
        <div className="space-y-1.5">
          <p className={s.header}>{t('where_to_next_header')}</p>
          {(disc.questions || []).map(q => (
            <button key={q.id} onClick={() => selectQuestion(q.text)} className={s.questionBtn}>{q.text}</button>
          ))}
          <FreeformEntry />
        </div>
      )}
      <button onClick={handleDismiss} className={s.dismissBtn}>{t('dismiss')}</button>
    </div>
  );
}

// AttrDiscoveryPanel — controlled component for "What else should I add?" flows.
// Same controlled pattern as CityDiscoveryPanel.
// primaryCity: if provided, fetches Google Places top results before the first AI call
// so the AI is aware of well-known local attractions and can include them.
export function AttrDiscoveryPanel({
  disc: rawDisc,
  setDisc,
  context,
  primaryCity,
  filters = [],
  existingAttrNames = new Set(),
  addingName,
  onAdd,
  onSnip,
  onDismiss,
  theme = 'light',
  triggerLabel,
  onTriggerClick,
  t: tProp,
  lang,
  autoOpen = false,
  initialQuestion = null,
  count,
}) {
  const t = tProp || defaultT;
  const disc = rawDisc || IDLE_ATTR;
  const dark = theme === 'dark';
  const resolvedTriggerLabel = triggerLabel ?? t('what_else_trigger', primaryCity?.split(',')[0]);

  const loadMoreSuggestions = async () => {
    const existingSuggs = disc.suggestions || [];
    const question = disc.lastQuestion || (filters.length > 0 ? `More ${filters.join(' and ')} attractions` : 'More attractions');
    setDisc({ phase: 'followup-loading' });
    try {
      const data = await callSuggest({
        type: 'attraction-followup',
        ...context,
        attractions: [...(context.attractions || []), ...existingSuggs.map(s => s.name)],
        filters,
        question,
        googlePlaces: disc.googlePlaces || [],
        language: lang,
        ...(count != null && { count }),
      });
      if (data.error || !data.suggestions?.length) { setDisc({ phase: 'error' }); return; }
      setDisc({ phase: 'suggestions', suggestions: data.suggestions, lastQuestion: disc.lastQuestion });
    } catch {
      setDisc({ phase: 'error' });
    }
  };

  const loadQuestions = async () => {
    track('discovery_panel_opened', { panel: 'attraction' });
    setDisc({ phase: 'loading', questions: [], suggestions: [], mode: null, freeformVisible: false, freeformText: '' });

    let googlePlaces = [];
    if (primaryCity) {
      try {
        const results = await fetchTopAttractions(primaryCity, lang);
        googlePlaces = results.map(p => p.name);
      } catch {}
    }

    try {
      const data = await callSuggest({ type: 'attraction-questions', ...context, filters, googlePlaces, language: lang, ...(count != null && { count }) });
      const suggestions = data.suggestions || [];
      setDisc({ phase: 'questions', questions: data.questions || [], suggestions, mode: data.mode, freeform: data.freeform, googlePlaces });
    } catch {
      setDisc({ phase: 'idle' });
    }
  };

  const selectQuestion = async (text) => {
    setDisc({ phase: 'followup-loading', lastQuestion: text });
    try {
      const data = await callSuggest({ type: 'attraction-followup', ...context, filters, question: text, googlePlaces: disc.googlePlaces || [], language: lang, ...(count != null && { count }) });
      if (data.error || !data.suggestions?.length) { setDisc({ phase: 'error' }); return; }
      setDisc({ phase: 'suggestions', suggestions: data.suggestions });
    } catch {
      setDisc({ phase: 'error' });
    }
  };

  const submitFreeform = async (text) => {
    if (!text.trim()) return;
    await selectQuestion(text);
    setDisc({ freeformVisible: false, freeformText: '' });
  };

  const handleDismiss = onDismiss || (() => { track('discovery_panel_dismissed', { panel: 'attraction' }); setDisc({ ...IDLE_ATTR }); });

  const attrAutoOpenFiredRef = React.useRef(false);
  React.useEffect(() => {
    if (autoOpen && !attrAutoOpenFiredRef.current && disc.phase === 'idle') {
      attrAutoOpenFiredRef.current = true;
      if (initialQuestion) {
        selectQuestion(initialQuestion);
      } else {
        loadQuestions();
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const s = dark ? {
    outerIdle: '',
    outerActive: '',
    loadText: 'text-xs text-white/60 animate-pulse',
    errText: 'text-xs text-red-300',
    retryBtn: 'text-xs text-white/80 hover:text-white transition-colors',
    backSmall: 'text-xs text-white/60 hover:text-white transition-colors',
    card: 'flex items-start gap-2 bg-white/10 rounded-xl px-3 py-2',
    name: 'text-xs font-medium text-white',
    reason: 'text-xs text-white/70 mt-0.5',
    addBtn: 'flex-shrink-0 mt-0.5 p-1 bg-white/20 hover:bg-white/30 text-white rounded-full disabled:opacity-50 transition-colors',
    snipBtn: 'flex-shrink-0 mt-0.5 p-1.5 text-white/30 hover:text-white/70 transition-colors rounded',
    backBtn: 'text-xs text-white/60 hover:text-white transition-colors',
    dismissBtn: 'text-xs text-white/30 hover:text-white/60 transition-colors',
    questionBtn: 'block w-full text-left text-xs px-3 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-colors',
    header: 'text-xs text-white/60',
    freeformBtn: 'flex items-center gap-2 w-full text-left text-xs px-3 py-2.5 rounded-xl border border-dashed border-white/20 text-white/45 hover:border-white/35 hover:text-white/70 transition-colors',
    input: 'flex-1 text-xs border border-white/20 rounded-lg px-2.5 py-1.5 focus:outline-none bg-white/10 text-white placeholder-white/40 focus:border-white/40',
    goBtn: 'text-xs px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg',
    noResults: 'text-xs text-white/60',
    triggerCls: 'text-xs text-white/50 hover:text-white transition-colors',
  } : {
    outerIdle: 'px-4 pb-4 pt-2 border-t border-gray-50',
    outerActive: 'px-4 pb-4 pt-3 border-t border-gray-50',
    loadText: 'text-xs text-gray-400 animate-pulse',
    errText: 'text-xs text-red-400',
    retryBtn: 'text-xs text-purple-500 hover:text-purple-700 transition-colors',
    backSmall: 'text-xs text-gray-400 hover:text-gray-500 transition-colors',
    card: 'flex items-start gap-2 bg-purple-50 rounded-xl px-3 py-2',
    name: 'text-xs font-medium text-gray-800',
    reason: 'text-xs text-gray-500 mt-0.5',
    addBtn: 'flex-shrink-0 mt-0.5 p-1 bg-purple-600 hover:bg-purple-700 text-white rounded-full disabled:opacity-50 transition-colors',
    snipBtn: 'flex-shrink-0 mt-0.5 p-1.5 text-gray-300 hover:text-purple-400 transition-colors rounded',
    backBtn: 'text-xs text-purple-400 hover:text-purple-600 transition-colors',
    dismissBtn: 'text-xs text-gray-300 hover:text-gray-400 transition-colors',
    questionBtn: 'block w-full text-left text-xs px-3 py-2 bg-white border border-gray-100 hover:border-purple-200 hover:bg-purple-50 rounded-xl text-gray-700 transition-colors',
    header: 'text-xs text-gray-400',
    freeformBtn: 'flex items-center gap-2 w-full text-left text-xs px-3 py-2.5 rounded-xl border border-dashed border-gray-200 bg-white text-gray-400 hover:border-purple-300 hover:text-purple-500 transition-colors',
    input: 'flex-1 text-xs border border-purple-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-purple-400',
    goBtn: 'text-xs px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700',
    noResults: 'text-xs text-gray-400',
    triggerCls: 'text-xs text-purple-400 hover:text-purple-600 transition-colors',
  };

  const FreeformEntry = () => (
    <>
      {!disc.freeformVisible && (
        <button onClick={() => setDisc({ freeformVisible: true })} className={s.freeformBtn}>
          <Pencil className="w-3 h-3 flex-shrink-0" />{t('freeform_cta')}
        </button>
      )}
      {disc.freeformVisible && (
        <div className="flex gap-1.5">
          <input autoFocus value={disc.freeformText || ''} onChange={e => setDisc({ freeformText: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') submitFreeform(disc.freeformText || ''); }} placeholder={t('describe_looking_for')} className={s.input} />
          <button onClick={() => submitFreeform(disc.freeformText || '')} className={s.goBtn}>{t('go')}</button>
        </div>
      )}
    </>
  );

  const SuggCard = ({ sug }) => {
    const isAdding = addingName === sug.name;
    return (
      <div key={sug.name} className={s.card}>
        <div className="flex-1 min-w-0">
          <span className={s.name}>{sug.name}</span>
          {sug.rating != null && (
            sug.placeId
              ? <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(sug.name)}&query_place_id=${sug.placeId}`} target="_blank" rel="noopener noreferrer" className="block text-xs text-amber-400 mt-0.5 hover:opacity-70 transition-opacity">
                  ★ {sug.rating.toFixed(1)}{sug.ratingCount != null && <span className={dark ? 'text-white/50' : 'text-gray-400'}> ({sug.ratingCount.toLocaleString()})</span>}
                </a>
              : <p className="text-xs text-amber-400 mt-0.5">
                  ★ {sug.rating.toFixed(1)}{sug.ratingCount != null && <span className={dark ? 'text-white/50' : 'text-gray-400'}> ({sug.ratingCount.toLocaleString()})</span>}
                </p>
          )}
          <p className={s.reason}>{sug.reason}</p>
        </div>
        {onSnip && <button onClick={() => onSnip('attraction', { name: sug.name, cityName: sug.city, types: sug.filter_tags, rating: sug.rating ?? null, ratingCount: sug.ratingCount ?? null, placeId: sug.placeId ?? null })} className={s.snipBtn} title={t('snip')}><Bookmark className="w-4 h-4" /></button>}
        <button onClick={() => { track('discovery_panel_suggestion_used', { panel: 'attraction', suggestion_name: sug.name }); onAdd(sug); }} disabled={!!addingName} className={s.addBtn}>
          {isAdding ? <span className="text-xs px-0.5">…</span> : <Plus className="w-3 h-3" />}
        </button>
      </div>
    );
  };

  const visible = (disc.suggestions || []).filter(sug => !existingAttrNames.has(sug.name.toLowerCase()));

  if (disc.phase === 'idle') {
    return <div className={s.outerIdle}><button onClick={onTriggerClick ?? loadQuestions} className={s.triggerCls}>{resolvedTriggerLabel}</button></div>;
  }
  if (disc.phase === 'loading' || disc.phase === 'followup-loading') {
    return <div className={s.outerActive}><p className={s.loadText}>{t('finding_ideas')}</p></div>;
  }
  if (disc.phase === 'error') {
    return (
      <div className={`${s.outerActive} space-y-2`}>
        <p className={s.errText}>{t('couldnt_load_suggestions')}</p>
        <div className="flex gap-2">
          <button onClick={() => selectQuestion(disc.lastQuestion)} className={s.retryBtn}>{t('retry')}</button>
          <button onClick={() => setDisc({ phase: 'questions' })} className={s.backSmall}>{t('back')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${s.outerActive} space-y-3`}>
      {disc.phase === 'questions' && visible.length > 0 && (
        <div className="space-y-1.5">
          {visible.map(sug => <SuggCard key={sug.name} sug={sug} />)}
          <button onClick={loadMoreSuggestions} className={s.questionBtn}>{t('more_attractions')}</button>
        </div>
      )}
      {disc.phase === 'suggestions' && (
        <div className="space-y-1.5">
          {visible.length > 0
            ? visible.map(sug => <SuggCard key={sug.name} sug={sug} />)
            : <p className={s.noResults}>{t('no_new_suggestions')}</p>}
          <button onClick={loadMoreSuggestions} className={s.questionBtn}>{t('more_attractions')}</button>
          <div className="pt-2"><FreeformEntry /></div>
        </div>
      )}
      {disc.phase === 'questions' && (disc.questions || []).length > 0 && (
        <div className="space-y-1.5">
          <p className={s.header}>{disc.mode === 'filtered' ? t('different_angle') : t('mood_for')}</p>
          {disc.questions.map(q => (
            <button key={q.id} onClick={() => selectQuestion(q.text)} className={s.questionBtn}>{q.text}</button>
          ))}
          <div className="pt-2"><FreeformEntry /></div>
        </div>
      )}
      <div className="flex items-center gap-3">
        {disc.phase === 'suggestions' && (
          <button onClick={() => setDisc({ phase: 'questions' })} className={s.backBtn}>{t('back_questions_short')}</button>
        )}
        <button onClick={handleDismiss} className={s.dismissBtn}>{t('dismiss')}</button>
      </div>
    </div>
  );
}
